import { createContext, useContext, FC, ReactNode } from 'react';
import { JourneyParams } from 'shared-types';
import { Guest } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { Rate } from '@features/rates/types';
import { Room } from 'shared-types/Room';
import { PackageType } from '../types';
import { SpaTimeBand } from '../helpers/spaTimeBands';
import { HeldSpaTime } from '../helpers/heldSpaTimes';

export interface PackageConfigurationContextValue {
  // Property info
  property: Property;
  language: string;

  // Journey/booking info
  journeyParams: JourneyParams;
  stayDuration: number;
  totalGuestAmount: number;

  // Package-specific config
  packageType: PackageType;

  // SPA-specific (if SPA package)
  spaPackageId: string;
  spaOpenTime: string;
  spaCloseTime: string;
  spaTimeBands?: Record<string, SpaTimeBand[] | undefined>;

  // Metadata
  rate: Rate & {
    type: 'rate';
    id: string;
  };
  room: (Partial<Room> | { name?: string }) & {
    type: 'room';
    id: string;
  };

  // Extras
  availableExtras?: any[];

  // Guest data
  basketGuests: Guest[];
  loggedInGuest: Guest;

  // Spa timeslots
  reservedSpaTimeSlots: string[];
  heldSpaTimes: HeldSpaTime[];

  // Feature flags resolved at the wizard root
  isRepeatedFlexibleEnabled: boolean;
  isEcom2Enabled: boolean;
}

const PackageConfigurationContext = createContext<PackageConfigurationContextValue | undefined>(
  undefined
);

export interface PackageConfigurationProviderProps {
  value: PackageConfigurationContextValue;
  children: ReactNode;
}

export const PackageConfigurationProvider: FC<PackageConfigurationProviderProps> = ({
  value,
  children,
}) => {
  return (
    <PackageConfigurationContext.Provider value={value}>
      {children}
    </PackageConfigurationContext.Provider>
  );
};

export const usePackageConfiguration = (): PackageConfigurationContextValue => {
  const context = useContext(PackageConfigurationContext);
  if (context === undefined) {
    throw new Error('usePackageConfiguration must be used within a PackageConfigurationProvider');
  }
  return context;
};
